/**
 * BrightSign bootstrap — runs before the React app mounts when the
 * screen bundle is hosted inside a BrightSign HTML zone.
 *
 * Responsibilities:
 *  1. Activate the BrightSign development shim if the URL asks for it
 *     (`?bsshim=1`) or the bundle is running on localhost (Chrome dev).
 *  2. Install the BrightSignBridge at window.TrillboardsNativeDevice so
 *     WebSensingBridge / fetch headers / DeviceManager can read platform
 *     identity without UA sniffing.
 *  3. Mark this device as running a native edge-AI agent so the web
 *     sensing pipeline skips camera/mic permission prompts (BrightSign
 *     players have no camera in phase 1).
 *  4. Persist the device serial number as the screen fingerprint so the
 *     overlay/main.js identity path picks it up via the existing
 *     localStorage.trillboard_fingerprint key.
 *  5. Hook console.log/warn/error so BrightSign DWS (Diagnostic Web
 *     Server) can capture the browser console through BSMessagePort.
 *  6. Resolve API / socket bases from URL query params if the autorun
 *     shell passed them; this lets BSN.cloud push new endpoints without
 *     rebuilding the bundle.
 *
 * This module must be side-effect only — index.js requires() it exactly
 * once when window.BSDeviceInfo is present.
 */

/* eslint-disable no-console */

// Shim must be evaluated BEFORE the bridge installer runs, because it
// populates window.BSDeviceInfo / window.BSMessagePort that the bridge
// reads from. On real BrightSign hardware the shim is a no-op — it only
// activates when the URL query says `?bsshim=1` or we're on localhost.
require('./shim');

const { installBrightSignBridge, deriveModelTier } = require('./BrightSignBridge');
const { installHeapWatchdog } = require('./HeapWatchdog');
const {
  installBrightSignDeviceCommandDispatcher,
} = require('./DeviceCommandDispatcher');
const SensorPodBridge = require('./SensorPodBridge');
const GpioCounterBridge = require('./GpioCounterBridge');
const runtimeAssetUrlModule = require('../../utils/runtimeAssetUrl');
const resolveRuntimeAssetUrl =
  runtimeAssetUrlModule.resolveRuntimeAssetUrl || runtimeAssetUrlModule.default;

const FINGERPRINT_KEY = 'trillboard_fingerprint';
const NATIVE_AGENT_KEY = 'trillboard_native_edge_agent';
const LS_SHELL_PATH = '/bs-ls.html';
const LS_MODE_FLAG = '__TRILL_BS_LS_MODE__';

/**
 * Safe localStorage setter — swallows exceptions so a locked-down
 * storage environment can't break bootstrap.
 */
function safeSetItem(key, value) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
      return true;
    }
  } catch (err) {
    // Private browsing / quota / security policy — silently continue.
  }
  return false;
}

/**
 * Safe localStorage getter — returns null on any failure.
 */
function safeGetItem(key) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(key);
    }
  } catch (err) {
    // fall through
  }
  return null;
}

/**
 * Send a console line to the host BrightScript shell via BSMessagePort.
 * BrightSign's DWS surfaces BSMessagePort messages in its live log view,
 * so this makes browser console output visible to the field engineer.
 */
function forwardToBSMessagePort(level, args) {
  try {
    if (
      typeof window === 'undefined' ||
      !window.BSMessagePort ||
      typeof window.BSMessagePort.PostBSMessage !== 'function'
    ) {
      return;
    }
    // Stringify each arg individually so objects survive transit. We
    // keep the payload small — BSMessagePort is not a bulk transport.
    const parts = [];
    for (let i = 0; i < args.length; i += 1) {
      const a = args[i];
      if (a === undefined) {
        parts.push('undefined');
      } else if (a === null) {
        parts.push('null');
      } else if (typeof a === 'string') {
        parts.push(a);
      } else {
        try {
          parts.push(JSON.stringify(a));
        } catch (_) {
          parts.push(String(a));
        }
      }
    }
    const message = parts.join(' ');
    // Truncate to 2KB — BSMessagePort has an internal buffer and we
    // don't want a runaway console.log to starve it.
    const truncated = message.length > 2048 ? message.slice(0, 2048) + '…' : message;
    window.BSMessagePort.PostBSMessage(
      JSON.stringify({ type: 'console', level, message: truncated })
    );
  } catch (err) {
    // Never throw from inside a console hook — would cause infinite
    // recursion on error logging.
  }
}

/**
 * Install console.log/warn/error shims that preserve the original
 * behaviour while ALSO forwarding to BSMessagePort. Tests can inspect
 * the installed flag via window.__TRILL_BS_CONSOLE_HOOKED__.
 */
function hookConsoleForwarding() {
  if (typeof window === 'undefined' || typeof console === 'undefined') return;
  if (window.__TRILL_BS_CONSOLE_HOOKED__) return; // idempotent
  const levels = ['log', 'warn', 'error', 'info'];
  for (let i = 0; i < levels.length; i += 1) {
    const level = levels[i];
    const original = console[level];
    if (typeof original !== 'function') continue;
    console[level] = function brightsignConsoleProxy() {
      try {
        original.apply(console, arguments);
      } catch (_) {
        // original might be a getter-only in some sandboxes; swallow
      }
      forwardToBSMessagePort(level, arguments);
    };
  }
  window.__TRILL_BS_CONSOLE_HOOKED__ = true;
}

/**
 * Fast, synchronous tier detection used by `maybeInstallLsShell()` to
 * decide whether to short-circuit React bootstrap. This deliberately
 * does NOT call through to DeviceManager — DeviceManager pulls in
 * ua-parser-js, which is one of the heaviest imports we're trying to
 * avoid on LS. A raw string-prefix check against BSDeviceInfo.model is
 * enough because BrightSign enforces the `LS|HD|XD|XT|XC` model
 * prefixes as part of their product naming convention.
 */
function detectBrightSignTier() {
  if (typeof window === 'undefined') return null;

  const hintedTier =
    window.__TRILL_BRIGHTSIGN_TIER__ ||
    (window.__TRILLBOARDS_BRIGHTSIGN && window.__TRILLBOARDS_BRIGHTSIGN.tier);
  if (typeof hintedTier === 'string' && hintedTier.length > 0) {
    return hintedTier.toLowerCase();
  }

  if (!window.BSDeviceInfo) return null;
  const rawModel = window.BSDeviceInfo.model;
  return deriveModelTier(rawModel);
}

/**
 * Derive the screen fingerprint from BSDeviceInfo.serialNumber using the
 * `brightsign-<serial>` convention. BrightSign serial numbers are globally
 * unique per chassis, so this gives us a stable device identity that
 * survives factory reset (which does NOT wipe the serial).
 */
function deriveFingerprintFromBSDeviceInfo() {
  if (typeof window === 'undefined' || !window.BSDeviceInfo) return null;
  const serial = window.BSDeviceInfo.serialNumber;
  if (typeof serial !== 'string' || serial.length === 0) return null;
  return 'brightsign-' + serial;
}

/**
 * Resolve API / socket base URLs from the URL query string. The autorun
 * shell passes these through when loading bootstrap.html so BSN.cloud
 * can point players at qa-api vs api without rebuilding the bundle.
 *
 * Only sets window.__TRILLBOARDS_*_BASE if the caller hasn't already
 * set them — avoids stomping on an explicit build-time override.
 */
function resolveRuntimeBasesFromQuery() {
  if (typeof window === 'undefined' || !window.location) return;
  let params;
  try {
    params = new URLSearchParams(window.location.search || '');
  } catch (err) {
    return;
  }
  const apiBase = params.get('apiBase') || params.get('api_base');
  const socketBase = params.get('socketBase') || params.get('socket_base');
  if (apiBase && !window.__TRILLBOARDS_API_BASE) {
    window.__TRILLBOARDS_API_BASE = apiBase.replace(/\/+$/, '');
  }
  if (socketBase && !window.__TRILLBOARDS_SOCKET_BASE) {
    window.__TRILLBOARDS_SOCKET_BASE = socketBase.replace(/\/+$/, '');
  }
}

/**
 * maybeInstallLsShell — pre-hook that runs BEFORE the regular BrightSign
 * bootstrap. When the detected tier is LS we redirect the page to the
 * minimal `bs-ls.html` shell (which never loads the React bundle) so the
 * LS-series Chromium heap never has to hold posthog, react-router,
 * IMA SDK, ContentCache IndexedDB, or any of the other heavy imports.
 *
 * Returns true if the shell was activated (caller should abort further
 * bootstrap), false otherwise.
 *
 * Guards:
 *   - If the current page is already `bs-ls.html` we do NOT redirect
 *     again (prevents an infinite reload loop).
 *   - The `__trill_ls_bypass__` query param disables the redirect so
 *     engineers can force the React bundle on LS hardware for debugging.
 *   - We set `window.__TRILL_BS_LS_MODE__ = true` so downstream code
 *     (notably `src/index.js`) can skip the React mount even when the
 *     redirect is disabled / in-flight.
 */
function maybeInstallLsShell() {
  if (typeof window === 'undefined') return false;
  const tier = detectBrightSignTier();
  if (tier !== 'ls') return false;

  // Always surface the tier for telemetry + downstream code, even when
  // the bypass / shell-page guards disable the redirect. LS_MODE_FLAG
  // is managed separately below because some branches (bypass / already
  // on bs-ls.html) want the React bundle to keep mounting normally.
  try { window.__TRILL_BRIGHTSIGN_TIER__ = 'ls'; } catch (_) { /* ignore */ }

  // Honour the engineer bypass flag — forces the full React bundle to
  // load on LS hardware, used for live debugging. Do NOT set
  // LS_MODE_FLAG here so `src/index.js` performs the normal mount.
  try {
    const params = new URLSearchParams((window.location && window.location.search) || '');
    if (params.get('__trill_ls_bypass__') === '1') {
      return false;
    }
  } catch (_) { /* ignore */ }

  // If we're already serving bs-ls.html, we're in the shell — do nothing.
  // LS_MODE_FLAG is left unset because the React bundle is not running
  // on this page anyway; nothing needs to be skipped.
  try {
    const pathname = (window.location && window.location.pathname) || '';
    if (pathname === LS_SHELL_PATH || /\/bs-ls\.html?$/i.test(pathname)) {
      return false;
    }
  } catch (_) { /* fall through */ }

  // Normal LS short-circuit path — set the mode flag so src/index.js
  // skips the React mount in the small window before navigation
  // actually commits.
  try { window[LS_MODE_FLAG] = true; } catch (_) { /* ignore */ }

  // Redirect to the LS shell, preserving the query string so
  // apiBase/socketBase overrides survive the navigation.
  // Use the screen bundle origin (not root-relative) because on real
  // BrightSign hardware the page boots from file:// and a root-relative
  // path like '/bs-ls.html' resolves to file:///bs-ls.html (non-existent).
  try {
    const search = (window.location && window.location.search) || '';
    const target = resolveRuntimeAssetUrl(LS_SHELL_PATH) + (search || '');
    if (typeof window.location.replace === 'function') {
      window.location.replace(target);
    } else {
      window.location.href = target;
    }
    return true;
  } catch (err) {
    // If replace() throws we still want to signal LS mode so index.js
    // can skip the React mount and avoid OOM.
    try { window[LS_MODE_FLAG] = true; } catch (_) { /* ignore */ }
    return true;
  }
}

/**
 * Run the full bootstrap sequence. Safe to call multiple times — each
 * step is idempotent.
 */
function bootstrap() {
  // Step 0: resolve API / socket base overrides from URL params BEFORE
  // the LS-tier check. This ensures window.__TRILLBOARDS_*_BASE globals
  // are set before any redirect, so the LS shell page inherits the
  // correct bases even if the redirect rewrites the URL.
  resolveRuntimeBasesFromQuery();

  // Step 0b: LS-tier short-circuit. If we're on an LS player we want to
  // delegate to the minimal shell before we touch localStorage, the
  // bridge, or anything else that might allocate heap. The function
  // returns true when it has decided to redirect — in that case we
  // abort the rest of bootstrap so we don't do redundant work during
  // the brief window before navigation starts.
  if (maybeInstallLsShell()) {
    return;
  }

  // Step 1: mark native edge agent active so WebSensingBridge disables
  // camera/mic prompts on players with no camera.
  safeSetItem(NATIVE_AGENT_KEY, 'true');

  // Step 2: install the JS bridge at window.TrillboardsNativeDevice.
  installBrightSignBridge();

  // Step 3: persist fingerprint. We only set the fingerprint if the
  // serial number is available and there's no existing fingerprint (or
  // the existing one is from a different platform — the brightsign-
  // prefix check lets us overwrite cross-platform contamination).
  const derived = deriveFingerprintFromBSDeviceInfo();
  if (derived) {
    const existing = safeGetItem(FINGERPRINT_KEY);
    if (!existing || existing !== derived) {
      safeSetItem(FINGERPRINT_KEY, derived);
    }
  }

  // Step 4: hook console forwarding so BSMessagePort sees browser logs.
  hookConsoleForwarding();

  // Step 6: expose a platform marker so other modules can branch cheaply
  // without re-reading BSDeviceInfo every call.
  try {
    window.__TRILL_PLATFORM__ = 'brightsign';
  } catch (_) {
    // window may be locked down; ignore.
  }

  // Step 7: install the JS heap watchdog on non-LS BrightSign tiers.
  // When we deliberately bypass the LS shell to show the standard QR /
  // pairing onboarding flow, the watchdog's current thresholds are too
  // aggressive and immediately reload the page before the operator can
  // pair the screen. Keep it enabled for higher-memory tiers until we
  // land LS-specific tuning.
  if (detectBrightSignTier() !== 'ls') {
    try {
      installHeapWatchdog();
    } catch (err) {
      // Never let the watchdog installer crash bootstrap — log once.
      try {
        // eslint-disable-next-line no-console
        console.warn('[BrightSign] HeapWatchdog install failed', err && err.message);
      } catch (_) { /* ignore */ }
    }
  }

  // Step 8: install the DeviceCommandDispatcher so the backend's
  // `deviceCommand` socket events are forwarded to bridge.brs. The
  // installer is idempotent and attaches to window.trillboardsSocket
  // (initialized later by overlay/main.js) via a bounded 10s poll.
  try {
    installBrightSignDeviceCommandDispatcher();
  } catch (err) {
    // The dispatcher is additive — if installation fails we still
    // want the rest of the bundle to boot. overlay/main.js has its
    // own limited deviceCommand handler (reboot.app / refresh.content /
    // device.clearCache) so baseline commands still work.
    try {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn(
          '[brightsign-bootstrap] failed to install DeviceCommandDispatcher',
          err
        );
      }
    } catch (_) {
      // swallow — never throw from bootstrap
    }
  }

  // Step 9 (Wave 2B): initialise the peripheral bridges. Both are
  // idempotent and guarded by BSDeviceInfo presence + a remote kill flag
  // (window.__TRILLBOARDS_BRIGHTSIGN_PERIPHERALS_DISABLED). They install
  // BSMessagePort subscribers that forward BrightScript-side sensor
  // events (sensor-pod-signal, audienceSignals from gpio-counter) onto the
  // existing Socket.IO audienceSignals pipeline.
  try {
    SensorPodBridge.init();
  } catch (_) {
    /* bridge init never blocks bootstrap */
  }
  try {
    GpioCounterBridge.init();
  } catch (_) {
    /* bridge init never blocks bootstrap */
  }
}

bootstrap();

export {
  bootstrap,
  maybeInstallLsShell,
  detectBrightSignTier,
  deriveFingerprintFromBSDeviceInfo,
  hookConsoleForwarding,
  resolveRuntimeBasesFromQuery,
  forwardToBSMessagePort,
  // Wave 2B — expose the peripheral bridge modules so other code can
  // directly introspect install state (e.g. the diagnostic overlay) and
  // so unit tests can reset them between cases.
  SensorPodBridge,
  GpioCounterBridge,
  FINGERPRINT_KEY,
  NATIVE_AGENT_KEY,
  LS_SHELL_PATH,
  LS_MODE_FLAG,
};
